import { useT } from '@/store';
import { daysUntil } from '@/lib/format';

/**
 * Document-expiry state for one date. Four states, not three: "no date on
 * file" is distinct from "valid", because an empty tech-inspection field is
 * a truck nobody has checked, not a truck that passed.
 *
 * Thresholds match the backend's compliance-alert engine (7 days critical,
 * 30 days warning) so a badge never disagrees with the alert a dispatcher
 * received about the same vehicle.
 */
export function ComplianceBadge({ date, label }) {
  const t = useT();
  const days = daysUntil(date);

  let tone = 'bg-white/6 text-dim';
  let text = t('transportas.badge.none');

  if (days !== null) {
    if (days < 0) {
      tone = 'bg-red/15 text-red';
      text = t('transportas.badge.expired');
    } else if (days <= 30) {
      tone = days <= 7 ? 'bg-red/15 text-red' : 'bg-amber/15 text-amber';
      text = `${t('transportas.badge.expiring')} · ${days} d.`;
    } else {
      tone = 'bg-green/15 text-green';
      text = t('transportas.badge.valid');
    }
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] ${tone}`}
      title={[label, date].filter(Boolean).join(': ')}
    >
      {text}
    </span>
  );
}

/** The three documents a vehicle carries, in one row. */
export function ComplianceBadges({ vehicle }) {
  const t = useT();
  return (
    <div className="flex flex-wrap gap-1">
      <ComplianceBadge date={vehicle.tech_inspection_expiry} label={t('transportas.techInspection')} />
      <ComplianceBadge date={vehicle.insurance_civil_expiry} label={t('transportas.insuranceCivil')} />
      {/* Kasko is optional cover — absent means "not carried", not "overdue",
          so it is only rendered when the fleet actually holds one. */}
      {vehicle.insurance_kasko_expiry ? (
        <ComplianceBadge date={vehicle.insurance_kasko_expiry} label={t('transportas.insuranceKasko')} />
      ) : null}
    </div>
  );
}
